import {
  creatorContactOf, eInvoiceFrom, normalizeCountry, renderTaxNoticeForRates, taxModeFromDb, type EInvoiceData, type EInvoiceExpected, type EInvoiceFinding, type EInvoiceProblem, type EInvoiceSummary,
} from "@talentkontor/domain";
import { talentPartyOf } from "../../model/invoice-document";
import type { CAttachment, CInvoice, CInvoiceLine, CPayout, TalentSelf } from "./data";
import type { DraftRecipient } from "./model";

/**
 * Steuerprüfung L12 im Creator-Portal: die eigene Rechnung zusätzlich als XRechnung (EN 16931, CII), mit derselben Erzeugung
 * wie die Gutschrift der Agentur (eInvoiceFrom aus packages/domain/src/e-invoice-input.ts). Nur für regelbesteuerte Creator in
 * Deutschland; Kleinunternehmer sind von der E-Rechnungspflicht ausgenommen (§ 34a Satz 3 UStDV) und bekommen kein Angebot.
 * Mit hochgeladen wird sie nur an Empfänger in Deutschland (Inlandsumsatz zwischen Unternehmern, § 14 Abs. 2 UStG);
 * herunterladen geht immer.
 */

export interface CreatorEInvoiceOffer {
  /** XRechnung herunterladen */
  download: boolean;
  /** beim Erzeugen der Rechnung als E-Rechnung mit ablegen */
  attach: boolean;
}

export function creatorEInvoiceOffer(talent: Pick<TalentSelf, "country" | "small_business">, invoice: Pick<CInvoice, "tax_mode"> | null, recipientCountry: string | null): CreatorEInvoiceOffer {
  const de = normalizeCountry(talent.country) === "DE";
  const regular = !talent.small_business && taxModeFromDb(invoice?.tax_mode ?? null) !== "SMALL_BUSINESS";
  const download = de && regular;
  return { download, attach: download && normalizeCountry(recipientCountry) === "DE" };
}

export interface CreatorEInvoiceInput {
  invoice: CInvoice;
  lines: readonly Pick<CInvoiceLine, "description" | "quantity" | "unit_cents" | "net_cents" | "tax_rate_bp">[];
  talent: TalentSelf;
  payout: CPayout | null;
  recipient: DraftRecipient;
  /** elektronische Adresse des Empfängers (Rechnungsadresse der Marke bzw. Antwortadresse der Agentur) */
  recipientEmail: string | null;
  /** Referenz des Empfängers (Bestellnummer der Kooperation), sonst gilt die Rechnungsnummer als Käuferreferenz */
  reference?: string | null;
}

/** XRechnung der Creator-Rechnung oder, wenn etwas fehlt, die fehlenden Angaben (gleiche Prüfung wie bei der Gutschrift) */
export function creatorEInvoice(x: CreatorEInvoiceInput): { ok: true; xml: string; filename: string } | { ok: false; problems: EInvoiceProblem[] } {
  const i = x.invoice;
  const party = talentPartyOf(x.talent as unknown as Record<string, unknown>) ?? { name: [x.talent.first_name, x.talent.last_name].filter(Boolean).join(" ") };
  const rates = [...new Set(x.lines.map((l) => l.tax_rate_bp ?? 0))];
  const lang = x.recipient.language;
  const p = x.payout;
  const issuer = {
    ...party,
    // Abnahme E-7: bei mehreren Sätzen „Enthält Umsatzsteuer (19 % und 7 %).“
    notice: i.notice_key ? renderTaxNoticeForRates(i.notice_key, lang, rates.map((bp) => (bp ?? 0) / 100)) : null,
    bank: p && (p.iban || p.account_number || p.payout_email)
      ? { iban: p.iban, bic: p.bic, holder: p.bank_account_holder ?? party.name, method: p.payout_method ?? "sepa", account: p.account_number ?? null, email: p.payout_email ?? null }
      : null,
  };
  const a = x.recipient.address;
  const recipient = {
    name: x.recipient.name, street: a?.street ?? null, postal_code: a?.postal_code ?? null, city: a?.city ?? null, country: normalizeCountry(x.recipient.country) ?? x.recipient.country,
    vat_id: x.recipient.vatId, email: x.recipientEmail, ...(x.reference ? { reference: x.reference, reference_kind: "order" } : {}),
  };
  const r = eInvoiceFrom({
    invoice: {
      number: i.number, issue_date: i.issue_date, currency: i.currency, self_billed: false, service_period_start: i.service_period_start, service_period_end: i.service_period_end,
      document_due_date: i.issuer_due_date ?? i.document_due_date, net_cents: i.net_cents, tax_cents: i.tax_cents, gross_cents: i.gross_cents, tax_mode: i.tax_mode,
      tax_eur_cents: i.tax_eur_cents ?? null, document_language: lang, issuer_snapshot: issuer, recipient_snapshot: recipient,
    },
    lines: x.lines.map((l) => ({ description: l.description, quantity: l.quantity, unit_cents: l.unit_cents, net_cents: l.net_cents, tax_rate_bp: l.tax_rate_bp })),
    seller: creatorContactOf(issuer as unknown as Record<string, unknown>, { phone: x.talent.whatsapp_phone, email: x.talent.email }),
    buyerEmail: x.recipientEmail,
  });
  return r.ok ? { ok: true, xml: r.xml, filename: r.filename } : { ok: false, problems: r.problems };
}

/** Werte der Rechnung im Portal, mit denen eine eingereichte E-Rechnung verglichen wird */
export function expectedOfInvoice(i: Pick<CInvoice, "number" | "issue_date" | "service_period_start" | "service_period_end" | "currency" | "net_cents" | "tax_cents" | "gross_cents">,
  talent: Pick<TalentSelf, "vat_id" | "tax_number"> | null, recipientName: string | null, month?: string | null): EInvoiceExpected {
  return {
    number: i.number, issueDate: i.issue_date, serviceFrom: i.service_period_start, serviceTo: i.service_period_end, currency: i.currency,
    netCents: i.net_cents, taxCents: i.tax_cents, grossCents: i.gross_cents, sellerVatId: talent?.vat_id ?? null, sellerTaxNumber: talent?.tax_number ?? null,
    buyerName: recipientName, month: month ?? null,
  };
}

/** Die E-Rechnung zu einer Rechnung: die Rechnungsdatei selbst, wenn sie eine ist, sonst die zuletzt abgelegte (z. B. die erzeugte XRechnung) */
export function eInvoiceAttachmentOf(files: readonly CAttachment[], invoiceId: string, pdfPath: string | null): (CAttachment & { e_invoice: EInvoiceSummary }) | null {
  const mine = files.filter((f): f is CAttachment & { e_invoice: EInvoiceSummary } => f.owner_id === invoiceId && !!f.e_invoice && (f.category ?? "invoice") === "invoice");
  return mine.find((f) => f.storage_path === pdfPath) ?? mine[mine.length - 1] ?? null;
}

/** Abweichungen, die das Einreichen sperren (Abnahme E-3): Nummer und Bruttobetrag, dazu die Währung (sonst wäre der Betrag ein anderer) */
export const E_INVOICE_BLOCKING = ["number", "gross", "currency"] as const;

const FIELD: Record<string, [string, string]> = {
  number: ["Rechnungsnummer", "invoice number"], gross: ["Bruttobetrag", "gross amount"], currency: ["Währung", "currency"], net: ["Nettobetrag", "net amount"],
  tax: ["Umsatzsteuer", "VAT"], issue_date: ["Rechnungsdatum", "invoice date"], service: ["Leistungszeitraum", "service period"], month: ["Leistungsmonat", "service month"],
  seller_tax_id: ["Steuernummer bzw. USt-IdNr.", "tax number or VAT ID"], buyer: ["Empfänger", "recipient"],
};

/**
 * Abweichungen der eingereichten E-Rechnung aufgeteilt: sperrende (Nummer, Brutto, Währung) und sonstige. Für die sonstigen der
 * Text des Hakens beim Einreichen: „Mir ist bewusst, dass meine E-Rechnung in … abweicht; maßgeblich ist die E-Rechnung.“
 */
export function splitEInvoiceFindings(findings: readonly EInvoiceFinding[], lang: "de" | "en" = "de"): { blocking: EInvoiceFinding[]; other: EInvoiceFinding[]; awareText: string | null } {
  const blocking = findings.filter((f) => (E_INVOICE_BLOCKING as readonly string[]).includes(f.key));
  const other = findings.filter((f) => !(E_INVOICE_BLOCKING as readonly string[]).includes(f.key));
  const names = other.map((f) => FIELD[f.key]?.[lang === "en" ? 1 : 0] ?? f.key);
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} ${lang === "en" ? "and" : "und"} ${names[names.length - 1]}` : names[0] ?? "";
  const awareText = other.length
    ? lang === "en" ? `I am aware that my e-invoice differs in ${list}; the e-invoice is authoritative.` : `Mir ist bewusst, dass meine E-Rechnung in ${list} abweicht; maßgeblich ist die E-Rechnung.`
    : null;
  return { blocking, other, awareText };
}

/**
 * Abnahme E-6: die Prüfmeldung „Bestätige, dass Nummer und Bruttobetrag … übereinstimmen“ passt nur zum Haken. Sperrt eine
 * Abweichung das Einreichen, nennt die Meldung die Abweichung; bei sonstigen Abweichungen verweist sie auf den umformulierten Haken.
 */
export function confirmFindingFor<C extends { errors: { key: string; text: string }[] }>(check: C, split: ReturnType<typeof splitEInvoiceFindings>, lang: "de" | "en" = "de"): C {
  if (!check.errors.some((e) => e.key === "pdf-confirm")) return check;
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const text = split.blocking.length
    ? t(`Deine E-Rechnung weicht ab: ${split.blocking.map((f) => f.text).join(" ")} Bitte zuerst angleichen.`, `Your e-invoice differs: ${split.blocking.map((f) => f.text).join(" ")} Please align it first.`)
    : split.awareText ? t("Bestätige den Hinweis zur Abweichung deiner E-Rechnung.", "Confirm the note on the differences in your e-invoice.") : null;
  return text ? { ...check, errors: check.errors.map((e) => (e.key === "pdf-confirm" ? { ...e, text } : e)) } : check;
}

export interface EInvoiceDraftSuggestion {
  number: string | null;
  issueDate: string | null;
  serviceFrom: string | null;
  serviceTo: string | null;
  termDays: number | null;
  /** Positionen mit Steuersatz der E-Rechnung in Basispunkten (Abnahme E-2), null = kein Satz angegeben */
  lines: { description: string; quantity: number; unitCents: number; totalCents: number; rateBp: number | null }[];
  grossCents: number | null;
  currency: string | null;
}

/**
 * Vorschlag für den Entwurf aus einer E-Rechnung (Einlesen in Schritt 2, wie beim PDF-Import): die Werte stehen im XML
 * eindeutig, Positionen mit Menge und Einzelpreis; Zahlungsziel aus Fälligkeit minus Rechnungsdatum.
 */
export function draftSuggestionFromEInvoice(d: EInvoiceData): EInvoiceDraftSuggestion {
  const days = d.issueDate && d.dueDate ? Math.round((Date.parse(d.dueDate) - Date.parse(d.issueDate)) / 86_400_000) : null;
  return {
    number: d.number, issueDate: d.issueDate, serviceFrom: d.serviceFrom ?? d.deliveryDate, serviceTo: d.serviceTo ?? d.deliveryDate,
    termDays: days !== null && days >= 0 && days <= 365 ? days : null,
    lines: d.lines.filter((l) => l.netCents !== null).map((l) => {
      const qty = l.quantity && l.quantity > 0 ? l.quantity : 1;
      const total = l.netCents!;
      const unit = l.unitPriceCents !== null && Math.abs(qty * l.unitPriceCents - total) <= Math.max(1, qty) ? l.unitPriceCents : Math.round(total / qty);
      return { description: (l.name ?? "").slice(0, 500), quantity: qty, unitCents: unit, totalCents: total, rateBp: l.ratePercent === null ? null : Math.round(l.ratePercent * 100) };
    }),
    grossCents: d.totals.grossCents, currency: d.currency,
  };
}
